import { useSyncExternalStore } from "react";
import { getIntroSteps } from "./profile/lendyIntroSteps";

/**
 * Whether there is something new to check on the assistant, for the pulsing dot on its avatar (the
 * new chat's and the rail's): a "Say hi" the person hasn't opened, or something in its inbox that
 * came in after they last opened its profile. Opening each clears its part.
 */
const INTRO_SEEN_KEY = "aav2-intro-seen";
const PROFILE_SEEN_KEY = "aav2-profile-seen-at";
// New intro screens light the dot again.
const INTRO_VERSION = getIntroSteps("").map(({ id }) => id).join(",");

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // It still clears for this visit.
  }
};

let state = {
  isIntroNew: read(INTRO_SEEN_KEY) !== INTRO_VERSION,
  profileSeenAt: Number(read(PROFILE_SEEN_KEY)) || 0,
  inboxNewestAt: 0,
};
const listeners = new Set();

const update = (changes) => {
  state = { ...state, ...changes };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

/** The assistant's inbox, as the header polls it: its newest item's time. */
export const reportInbox = (inbox) => {
  const newestAt = Math.max(0, ...(inbox?.items || []).map(({ createdAt }) => Number(createdAt) || 0));
  if (newestAt !== state.inboxNewestAt) {
    update({ inboxNewestAt: newestAt });
  }
};

export const markIntroSeen = () => {
  write(INTRO_SEEN_KEY, INTRO_VERSION);
  update({ isIntroNew: false });
};

export const markProfileSeen = () => {
  const now = Date.now();
  write(PROFILE_SEEN_KEY, String(now));
  update({ profileSeenAt: now });
};

/** { isIntroNew, hasInboxNews, hasNews } */
export const useAgentNews = () => {
  const { isIntroNew, profileSeenAt, inboxNewestAt } = useSyncExternalStore(subscribe, () => state);
  const hasInboxNews = inboxNewestAt > profileSeenAt;

  return { isIntroNew, hasInboxNews, hasNews: isIntroNew || hasInboxNews };
};

// The new chat's avatar asks Chat.js to open the profile when what's new is in there.
export const OPEN_PROFILE_EVENT = "aav2-open-profile";
